import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * WipStatusCard
 *
 * Props:
 *   label       — string, e.g. 'Total WIP'
 *   count       — number
 *   requestType — DB request_type string or 'all'
 *   year        — string (optional, not currently used for navigation)
 *   loading     — bool
 *   accentColor — string hex color for top border and icon background
 *   icon        — React element (MUI icon)
 */
const WipStatusCard = ({
  label = '',
  count = 0,
  requestType = 'all',
  year = '',
  loading = false,
  accentColor = '#003781',
  icon = null,
  onViewAll = null,
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    let url = '/track-requests?request_status=Work+in+Progress';
    if (requestType && requestType !== 'all') {
      url += `&request_type=${encodeURIComponent(requestType)}`;
    }
    navigate(url);
  };

  const handleViewAll = (e) => {
    e.stopPropagation();
    if (onViewAll) {
      onViewAll();
    } else {
      navigate('/data-view/wip-summary');
    }
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        cursor: 'pointer',
        background: 'linear-gradient(135deg, #EBF2FB 0%, #f5f8fd 100%)',
        borderRadius: '12px',
        boxShadow: '0 2px 16px rgba(0,55,129,0.10)',
        borderTop: `4px solid ${accentColor}`,
        minWidth: 140,
        flex: 1,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.22s, transform 0.22s',
        '&:hover': {
          boxShadow: '0 8px 28px rgba(0,55,129,0.18)',
          transform: 'translateY(-3px)',
        },
      }}
    >
      {/* Icon + count row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: `${accentColor}1f`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': { color: accentColor, fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        )}
        {loading ? (
          <CircularProgress size={28} sx={{ color: accentColor }} />
        ) : (
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ color: accentColor, lineHeight: 1.1, fontSize: '2rem' }}
          >
            {count}
          </Typography>
        )}
      </Box>

      {/* Label */}
      <Typography
        variant="body2"
        sx={{ color: '#6b7280', fontWeight: 500, lineHeight: 1.3 }}
      >
        {label}
      </Typography>

      {/* View all link */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
        <Typography
          variant="caption"
          onClick={handleViewAll}
          sx={{
            color: accentColor,
            fontWeight: 600,
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            '&:hover': { opacity: 0.75 },
          }}
        >
          View all
        </Typography>
      </Box>
    </Box>
  );
};

export default WipStatusCard;
